import { usePage } from "@inertiajs/react"
import { UserLayout } from "@/components/layouts/user-layout"
import { EntryAskPanel } from "@/features/user/entry-ask-panel"
import { MenuScreen } from "@/features/user/menu-screen"
import type { PendingEntryRequest } from "@/features/user/entry-state"
import type { MenuPayload } from "@/features/user/types"
import { t } from "@/lib/i18n"

type EntryPayload = {
  token: string
  locationName: string
  tableName: string | null
  maxNameLength: number
  pendingRequest: PendingEntryRequest | null
}

type Props = {
  menu: MenuPayload
  entry: EntryPayload
}

/**
 * What scanning the table's printed QR shows before anyone has opened it: the
 * whole menu, read-only, with the door pinned under it. `MenuScreen` renders
 * in its existing `readOnly` mode rather than a fork, so this screen and
 * `/dinein/menu` can never disagree about how a dish looks — and `EntryAskPanel`
 * owns the one action this screen offers, so this page stays a thin composition
 * of the two.
 */
export default function UserInvitesMenu() {
  const { menu, entry } = usePage<Props>().props

  return (
    <UserLayout wide fillsViewport locationName={entry.locationName}>
      <div className="flex min-h-full flex-col" data-testid="invite-menu-view">
        <p
          className="mx-4 mt-3 rounded-xl border bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
          data-testid="invite-menu-read-only-note"
        >
          {t("ui.react.pages.user.invites.menu.read_only_note")}
        </p>

        <MenuScreen menu={menu} readOnly />

        <EntryAskPanel
          token={entry.token}
          maxNameLength={entry.maxNameLength}
          pendingRequest={entry.pendingRequest}
        />
      </div>
    </UserLayout>
  )
}
